import { WebStorageStateStore } from 'oidc-client-ts';
import { logger } from 'features/logger/logger';
import {
  IDENTITY_REQUEST_TIMEOUT_SECONDS,
  LOCAL_STORAGE_AUTH_RETURN_URL,
} from '../constants';
import type { OidcMetadata, User } from 'oidc-client-ts';
import type { AuthProviderProps } from 'react-oidc-context';

const authority = import.meta.env.PUBLIC_IDENTITY_ISSUER;
const clientId = import.meta.env.PUBLIC_IDENTITY_CLIENT_ID;

const identityUserStoreKey = `oidc.user:${authority}:${clientId}`;

/** Whether oidc-client-ts holds an Identity user in `localStorage`. */
export const hasStoredIdentityUser = (): boolean =>
  window.localStorage.getItem(identityUserStoreKey) !== null;

type IdentityMetadata = Pick<
  OidcMetadata,
  'authorization_endpoint' | 'token_endpoint' | 'end_session_endpoint'
>;

// Castor Identity's endpoints under its issuer; none without a valid issuer.
export const identityMetadata = (
  issuer: string | undefined
): IdentityMetadata | undefined => {
  if (!issuer) {
    return undefined;
  }

  try {
    const base = new URL(issuer);
    base.pathname = `${base.pathname.replace(/\/+$/, '')}/protocol/openid-connect/`;
    base.search = '';
    base.hash = '';
    const endpoint = (name: string) => new URL(name, base).href;

    return {
      authorization_endpoint: endpoint('auth'),
      token_endpoint: endpoint('token'),
      end_session_endpoint: endpoint('logout'),
    };
  } catch {
    return undefined;
  }
};

export const oidcConfig: AuthProviderProps = {
  authority,
  metadata: identityMetadata(authority),
  client_id: clientId,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: window.location.origin,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  // Safari ITP may clear localStorage entries written before a cross-origin redirect
  // chain (app → IdP → app), causing "No matching state found in storage" on callback.
  // sessionStorage is tab-scoped and survives same-tab redirects without ITP interference.
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
  // Cross-tab logout runs through the auth_logout signal.
  monitorSession: false,
  // Renewal is driven by useActivityGatedTokenRenewal so that an idle tab
  // stops refreshing the Identity session.
  automaticSilentRenew: false,
  silent_redirect_uri: `${window.location.origin}/silent-renew.html`,
  requestTimeoutInSeconds: IDENTITY_REQUEST_TIMEOUT_SECONDS,
  silentRequestTimeoutInSeconds: IDENTITY_REQUEST_TIMEOUT_SECONDS,
  // Requested scopes
  scope: 'openid profile email',
  // Identity reuses a live session, such as the enrollment portal's, only for
  // a request at level 0; without a session it still asks for the email code.
  acr_values: '0',
  // Handle the signin callback to prevent redirect loops
  onSigninCallback: (user: User | undefined) => {
    // Extract return URL from OAuth state parameter
    const state = user?.state as
      | {
          returnUrl?: string;
        }
      | undefined;
    const returnUrl = state?.returnUrl;

    logger.debug('onSigninCallback received state', {
      details: {
        hasState: !!user?.state,
        returnUrl: returnUrl ?? 'none',
      },
    });

    if (returnUrl && typeof returnUrl === 'string') {
      // Save to localStorage for AuthCallback to consume
      localStorage.setItem(LOCAL_STORAGE_AUTH_RETURN_URL, returnUrl);
    } else {
      localStorage.removeItem(LOCAL_STORAGE_AUTH_RETURN_URL);
      logger.debug('No return URL in OAuth state', {
        details: {
          stateType: typeof user?.state,
        },
      });
    }

    // Remove OIDC params from URL after successful signin
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};
